import Head from 'next/head'
import React from 'react'
import AdaptiveThemeBox from '../../atoms/AdaptiveThemeBox'
import HugeTitle from '../../atoms/HugeTitle'
import ThemeBox from '../../atoms/ThemeBox'
import ThemedBackground from '../../atoms/ThemedBackground'
import { CasesShared, KnowledgePages } from '../../content/types'
import { useLocale } from '../../hooks/useLocale'
import AuthorWrittenBy from '../../molecules/AuthorWrittenBy'
import BackLink from '../../molecules/BackLink'
import Container from '../../molecules/Container'
import Footer from '../../organisms/Footer'
import HeaderBar from '../../organisms/HeaderBar'
import ProjectsContent from '../../organisms/ProjectsContent'
import { contentPathToURLs } from '../../utils/contentPathToURLs'
import { formatDate } from '../../utils/formatDate'
import MetaInformation from '../../utils/MetaInformation'
import { GetStaticPaths } from 'next'
import ContactUsCTA from '@/organisms/ContactCTA'

type StaticPathType = {
  params: {
    slug: string
  }
}

export const getStaticPaths: GetStaticPaths = async (context) => {
  // the only paths that are allowed will be slugs that have a corresponding markdown
  // file in the content/cases directory. the `fallback: false` means that a 404
  // will be generated for all other urls. moreover, this method is required
  // for use with the `next export` command, which generates the static site.
  let paths: StaticPathType[] = contentPathToURLs('content/knowledge').map(
    (f) => {
      return { params: { slug: f } }
    },
  )

  if (context.locales) {
    paths = paths.flatMap((path) =>
      context.locales.map((locale) => ({ ...path, locale })),
    )
  }

  return { paths: paths, fallback: false }
}

export async function getStaticProps({ params }: StaticPathType) {
  // this is safe because of the getStaticPaths function, which only allows slugs
  // that match these files.
  // changing the corresponding markdown file will not trigger a live-reload in
  // nextjs, but the alternative is using an entirely different markdown parser.
  const { attributes } = require(`../../content/knowledge/${params.slug}.md`)

  const { attributes: shared_attributes } = require(
    `../../content/cases_shared.md`,
  )

  return { props: { attributes, shared_attributes, slug: params.slug } }
}

export default function KnowledgePageSingle({ attributes, shared_attributes }) {
  const showContact = true
  const { translations, locale } = useLocale<KnowledgePages>(attributes)
  const { translations: shared_translations } =
    useLocale<CasesShared>(shared_attributes)

  return (
    <>
      <Head>
        {/* NOTE: the <title> tag gets its contents through string interpolation. If we use JSX syntax to insert <t>{t.name} | Abl</t> the */}
        {/* JSX compiler will embed an empty html comment due to the way {} gets expanded, which causes it to show up in titles. */}
        <title>{`${translations.name} | Researchable`}</title>
        <MetaInformation locale={locale} {...translations.meta} />
      </Head>
      <AdaptiveThemeBox defaultTheme="bright">
        <HeaderBar />
      </AdaptiveThemeBox>
      <ThemeBox theme="bright">
        <ThemedBackground>
          <Container>
            <div className="inline-block py-10">
              <BackLink href="/knowledge">
                {shared_translations.overview}
              </BackLink>
            </div>
            <div className="xl:mx-60">
              <HugeTitle>{translations.name}</HugeTitle>
              <div className="my-8">
                <AuthorWrittenBy
                  author={translations.author_name}
                  img={translations.author_image}
                  when={formatDate(translations.published_date)}
                />
              </div>
              <ProjectsContent content={translations.content} />
            </div>
            {showContact && <ContactUsCTA />}
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="black">
        <Footer />
      </ThemeBox>
    </>
  )
}
